/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/phone/QaPhoneWorkspace.tsx
 * FILE_ID: JOBHUB-QA-PHONE-WORKSPACE-001
 * PURPOSE: Phone QA frame. Mounts instead of the desktop toolbar + accordion + ITP modal.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { snapshotQaPhotoViews, useHeaderMobileTitleSlot, type ContextMenuItem, type QaNote } from '@quotech/ui'
import type { QaAreaExecution, QaFloor } from '../../../qa/qaApi'
import {
  areaItems,
  areaProgress,
  checklistGroups,
  groupEvidenceByItem,
  normalizeList,
} from '../../../qa/qaApiHelpers'
import { JobQaInspectionSection } from '../JobQaInspectionSection'
import { QaLockedAreaBanner } from '../QaLockedAreaBanner'
import { resolveItpScope, selectedQaUnion, type QaItpRequest } from '../qaItpScope'
import { groupNameOf, levelsOf } from '../qaTabSelectors'
import { useQaItpExecutions } from '../useQaItpExecutions'
import type { JobQaTabController } from '../useJobQaTabController'
import type { QaAreaMode } from '../../JobQaModeToggle'
import type { ItpCoverFields } from '../../../qa/itpCover'
import { flushPendingQuoteDetails, useResolvedItpReportTitle } from '../useJobQuoteDetails'
import { buildItpRequestHtml } from '../../../qa/itpDocument'
import { areasWithEvidenceUrls } from '../useQaEvidenceUrls'
import { defaultItpLibraryName } from '../../../qa/itpLibraryName'
import { QaItpLibraryNameDialog } from '../QaItpLibraryNameDialog'
import { saveItpLibraryEntry } from '../saveItpLibraryEntry'
import { useQaApplyDrag } from '../useQaApplyDrag'
import { QaDragRoot } from '../../../qa/useQaPointerDrag'
import { QA_PHONE_CONTROLS } from './qaPhoneControlInventory'
import { phoneVisibleSelection } from './qaPhoneSelectAll'
import { QA_PHONE_TITLE_ACTIONS_SLOT_ID } from '../../../qa/phonePage'
import { QaItpExportView } from '../itp-export'
import { QaPhoneBuildList } from './QaPhoneBuildList'
import { QaPhoneHeader } from './QaPhoneHeader'
import { QaPhoneItpList } from './QaPhoneItpList'
import { QaPhoneModeMenu } from './QaPhoneModeMenu'
import { QaPhoneRunList } from './QaPhoneRunList'
import { QaPhoneScrollRail, QA_PHONE_RAIL_LANE_PX } from './QaPhoneScrollRail'
import { QaPhoneSheetHost } from './QaPhoneSheetHost'
import { QaPhoneTitleActions } from './QaPhoneTitleActions'
import { qaPhoneRenameRoute, useQaPhoneUi } from './useQaPhoneUi'

export function QaPhoneWorkspace({
  controller,
  cover,
  checklistMenuItems,
  renameChecklistId,
  onRenameChecklistStarted,
}: {
  controller: JobQaTabController
  cover?: ItpCoverFields
  checklistMenuItems?: (id: string, name: string, areaId: string) => ContextMenuItem[]
  renameChecklistId?: string | null
  onRenameChecklistStarted?: () => void
}) {
  const { api, props, selection, workspace } = controller
  const ui = useQaPhoneUi()
  const reportTitle = useResolvedItpReportTitle(props.jobId)
  const queryClient = useQueryClient()
  const [itpShowAll, setItpShowAll] = useState(false)
  const [exportOpen, setExportOpen] = useState(false)
  const [libraryOpen, setLibraryOpen] = useState(false)
  const [libraryHtml, setLibraryHtml] = useState('')
  const [libraryBusy, setLibraryBusy] = useState(false)
  const [libraryError, setLibraryError] = useState<string | null>(null)
  const [libraryRequest, setLibraryRequest] = useState<QaItpRequest | null>(null)
  const openExport = () => {
    void flushPendingQuoteDetails().then(() => {
      ui.closeSheet()
      setExportOpen(true)
    })
  }
  const [toast, setToast] = useState<string | null>(null)
  const areas = normalizeList<QaAreaExecution>(workspace.areasQuery.data)
  const areaPickFor = useRef<string | null>(null)
  useEffect(() => {
    const inspectionId = workspace.activeInspectionId
    if (!inspectionId || areaPickFor.current === inspectionId) return
    if (workspace.selectedAreaId !== null) return
    if (workspace.areasQuery.isLoading || areas.length === 0) return
    areaPickFor.current = inspectionId
    ui.setOpenSheet('scope-area')
  }, [
    areas.length,
    ui.setOpenSheet,
    workspace.activeInspectionId,
    workspace.areasQuery.isLoading,
    workspace.selectedAreaId,
  ])
  const areaIds = areas.map((area) => area.id)
  const { executions, pending } = useQaItpExecutions(areaIds, api, workspace.areaExecutionsReady)
  const request = resolveItpScope({
    selected: selectedQaUnion(selection.visibleOf, executions, areaIds, selection.excludedChecks),
    activeAreaId: workspace.activeAreaId,
    activeChecklistId: workspace.activeChecklistId,
    allChecklists: workspace.allChecklists,
    executions,
    selectionTouched: selection.touched,
  })
  const beginLibrarySave = (html: string, next: QaItpRequest = request) => {
    if (next.areaIds.length === 0) {
      setToast('Select checks, a checklist, or an area first.')
      return
    }
    setLibraryHtml(html)
    setLibraryRequest(next)
    setLibraryError(null)
    setLibraryOpen(true)
    ui.closeSheet()
  }
  const currentLibraryHtml = async () => {
    const ready = await areasWithEvidenceUrls(queryClient, api, executions)
    return buildItpRequestHtml(
      ready,
      props.jobId,
      request.areaIds,
      request.checklistIds,
      request.itemIds ? new Set(request.itemIds) : undefined,
      reportTitle,
      snapshotQaPhotoViews(),
      cover,
    )
  }
  const itpItemKey = request.itemIds?.join(',')
  useEffect(() => { setItpShowAll(false) }, [itpItemKey])
  const { toggleSheet } = ui
  useEffect(() => {
    const onOpen = (event: Event) => {
      const sheetId = (event as CustomEvent<{ id?: string }>).detail?.id
      if (sheetId === 'select-qa') toggleSheet('select-qa')
    }
    window.addEventListener('qa-phone-open-sheet', onOpen)
    return () => window.removeEventListener('qa-phone-open-sheet', onOpen)
  }, [toggleSheet])

  const execution = workspace.areaExecQuery?.data
  const groups = execution ? checklistGroups(execution) : []
  const shownChecklistId = workspace.allChecklists ? null : workspace.activeChecklistId
  const tableIds = groups
    .map((group) => group.checklist.id)
    .filter((id) => !shownChecklistId || id === shownChecklistId)
  const renameRoute = qaPhoneRenameRoute(ui.buildView, renameChecklistId ?? null, tableIds)
  const setOpenSheet = ui.setOpenSheet
  useEffect(() => {
    if (renameRoute.sheet) setOpenSheet('scope-checklist')
  }, [renameRoute.sheet, setOpenSheet])
  useEffect(() => {
    if (renameRoute.drop) onRenameChecklistStarted?.()
  }, [renameRoute.drop, onRenameChecklistStarted])
  const items = execution ? areaItems(execution) : []
  const progress = useMemo(() => {
    if (!execution) return { done: 0, total: 0, percent: 0 }
    const scoped = workspace.allChecklists || !workspace.activeChecklistId
      ? execution
      : { checklists: execution.checklists.filter((row) => row.id === workspace.activeChecklistId) }
    return areaProgress(scoped)
  }, [execution, workspace.activeChecklistId, workspace.allChecklists])

  const scope = selection.scope ?? 'areas'
  const pool = scope === 'checklists' ? groups.map((g) => g.checklist.id)
    : scope === 'checks' ? items.map((item) => item.id) : areaIds
  const visibleIds = selection.visibleOf(scope === 'checks' ? 'checks' : scope, pool)
  const titleSlot = useHeaderMobileTitleSlot(QA_PHONE_TITLE_ACTIONS_SLOT_ID)
  const scrollRef = useRef<HTMLDivElement>(null)
  const visibleSelect = phoneVisibleSelection({
    areaSheetOpen: ui.openSheet === 'scope-area',
    allChecklists: workspace.allChecklists,
    areaIds,
    checklistIds: groups.map((group) => group.checklist.id),
    itemIds: items.map((item) => item.id),
  })
  const selectedVisible = selection.visibleOf(visibleSelect.scope, visibleSelect.ids)
  const allVisibleSelected = visibleSelect.ids.length > 0 && selectedVisible.length === visibleSelect.ids.length
  const onToggleSelectAll = () => {
    if (allVisibleSelected) {
      selection.clear()
      ui.setSelecting(() => false)
      return
    }
    selection.selectAll(visibleSelect.scope, visibleSelect.ids)
    ui.setSelecting(() => true)
  }
  // The bar counts the whole accumulated union (areas + checklists + checks,
  // across every loaded area), so switching checklist/area never zeroes it.
  const selectedUnion = selectedQaUnion(selection.visibleOf, executions, areaIds, selection.excludedChecks)
  const selectedTotal =
    selectedUnion.areas.length + selectedUnion.checklists.length + selectedUnion.checks.length
  const activeArea = areas.find((area) => area.id === workspace.activeAreaId)
  const activeGroup = groups.find((g) => g.checklist.id === workspace.activeChecklistId)
  const floors = normalizeList<QaFloor>(workspace.floorsQuery.data)
  const levels = levelsOf(floors, areas)
  const applyDrag = useQaApplyDrag(controller)
  const levelRows = levels
    .filter(({ floor }) => Boolean(floor && floor.id !== floors[0]?.id))
    .map(({ floor, areas: levelAreas }) => ({
      id: floor!.id,
      name: floor!.floor_name,
      empty: levelAreas.length === 0,
    }))
  const areaRows = levels.flatMap(({ areas: levelAreas }) =>
    levelAreas.map((area) => ({
      id: area.id,
      name: area.area_name,
      group: groupNameOf(area, floors) || undefined,
    })),
  )
  const selectedCheckIds = selection.visibleOf('checks', items.map((item) => item.id))
  const selectedNote = selectedCheckIds.length === 1
    ? items.find((item) => item.id === selectedCheckIds[0])
    : undefined
  const noteItem = items.find((item) => item.id === ui.expandedRowId)
    ?? selectedNote
    ?? (items.length === 1 ? items[0] : undefined)
  const notes: QaNote[] = noteItem
    ? (groupEvidenceByItem(execution?.evidence ?? [])[noteItem.id] ?? [])
      .filter((ev) => ev.type === 'note')
      .map((ev) => ({
        id: ev.id,
        text: ev.note_text ?? '',
        authorName: ev.captured_by_name,
        at: ev.captured_at ?? '',
      }))
    : []

  const hasItpSelection = (request.itemIds?.length ?? 0) > 0
  const itpRequest = hasItpSelection ? request : { ...request, itemIds: null }

  return (
    <QaDragRoot onDrop={applyDrag}>
    <div className="relative flex h-full min-h-0 flex-col overflow-hidden" data-testid="qa-phone-workspace">
      {/* The head sits above the scroller. It is not sticky, so the first heading can scroll into view. */}
      <div
        className="relative z-30 mx-[var(--qa-phone-inset,0px)] shrink-0 bg-card"
        data-testid="qa-phone-sticky-head"
      >
        {workspace.inspections.length > 1 && !titleSlot ? (
          <div className="border-b border-border bg-card px-2 py-1">
            {/* Reused from: packages/job-hub/src/jobs/tabs/job-qa/JobQaInspectionSection.tsx (JobQaInspectionSection) */}
            <JobQaInspectionSection
              workspace={workspace}
              compact
              chipsOnly
              jobId={controller.props.jobId}
              orgId={controller.props.orgId}
              quotes={controller.props.quotes}
            />
          </div>
        ) : null}
        <QaPhoneHeader
          areaName={activeArea?.area_name ?? 'Location'}
          checklistName={workspace.allChecklists ? 'All checklists' : (activeGroup?.name ?? null)}
          checklistCount={!workspace.allChecklists && activeGroup ? `${activeGroup.done}/${activeGroup.total}` : null}
          done={progress.done}
          total={progress.total}
          percent={progress.percent}
          onOpenScope={() => ui.toggleSheet('scope-area')}
          onOpenChecklist={() => ui.toggleSheet('scope-checklist')}
          onOpenItp={openExport}
          buildView={ui.buildView}
          onToggleBuildView={workspace.areaMode === 'build' ? ui.toggleBuildView : undefined}
        />
      </div>
      <div data-testid="qa-phone-scroll-frame" className="relative flex min-h-0 flex-1 flex-col">
      {/* Reused from: packages/ui/src/kanban/components/email-rail/EmailRailBody.tsx ([touch-action:pan-y]) */}
      <div
        ref={scrollRef}
        data-testid="qa-phone-scroll"
        className={`min-h-0 flex-1 overflow-y-auto [touch-action:pan-y]${titleSlot ? ' [scrollbar-width:none] [&::-webkit-scrollbar]:hidden' : ''}`}
        style={{ paddingRight: titleSlot ? QA_PHONE_RAIL_LANE_PX : 4 }}
      >
        {workspace.checklistReady === false || (workspace.areaExecQuery?.isLoading && !execution) ? (
          <div data-testid="qa-phone-checklist-skeleton" className="grid gap-2 p-3" aria-hidden>
            <div className="h-16 rounded-lg bg-muted" />
            <div className="h-16 rounded-lg bg-muted" />
            <div className="h-16 rounded-lg bg-muted" />
          </div>
        ) : null}
        {workspace.checklistReady !== false && !(workspace.areaExecQuery?.isLoading && !execution) && execution?.status === 'locked' && (
          <div data-qa-phone-control={QA_PHONE_CONTROLS.unlock}>
            <QaLockedAreaBanner
              area={execution}
              onUnlock={execution.can_sign_off ? () => {
                void api.unlockArea(execution.id, 'Unlock to edit').then(workspace.invalidateExecution)
              } : undefined}
            />
          </div>
        )}
        {workspace.checklistReady !== false && !(workspace.areaExecQuery?.isLoading && !execution) && workspace.areaMode === 'run' && (
          <QaPhoneRunList controller={controller} onOpenItp={() => workspace.setAreaMode('itp')} />
        )}
        {workspace.checklistReady !== false && !(workspace.areaExecQuery?.isLoading && !execution) && workspace.areaMode === 'build' && (
          <QaPhoneBuildList
            controller={controller}
            view={ui.buildView}
            checklistMenuItems={checklistMenuItems}
            renameChecklistId={renameRoute.table}
            onRenameChecklistStarted={onRenameChecklistStarted}
          />
        )}
        {workspace.checklistReady !== false && !(workspace.areaExecQuery?.isLoading && !execution) && workspace.areaMode === 'itp' && (
          <QaPhoneItpList
            controller={controller}
            request={{
              ...request,
              checklistIds: workspace.allChecklists || !activeGroup ? null : [activeGroup.checklist.id],
            }}
            areas={execution ? [execution] : []}
            pending={pending}
            showAll={itpShowAll}
            onShowAll={() => setItpShowAll(true)}
          />
        )}
      </div>
      {titleSlot ? <QaPhoneScrollRail scrollerRef={scrollRef} /> : null}
      </div>
      {toast && (
        <p
          role={toast.startsWith('Could') ? 'alert' : 'status'}
          className="absolute inset-x-3 bottom-14 z-10 mx-[var(--qa-phone-inset,0px)] rounded-md border border-border bg-card px-3 py-2 text-xs shadow"
        >
          {toast}
          <button type="button" className="ml-2 underline" onClick={() => setToast(null)}>Dismiss</button>
        </p>
      )}
      <QaPhoneModeMenu
        mode={workspace.areaMode}
        onModeChange={(mode: QaAreaMode) => {
          if (workspace.areaMode === 'build' && (mode === 'run' || mode === 'itp')) {
            void workspace.leaveBuild(mode)
            return
          }
          workspace.setAreaMode(mode)
        }}
        onMore={() => ui.toggleSheet('more')}
        onExport={openExport}
        selectedCount={selectedTotal}
        onAssign={() => ui.toggleSheet('assign')}
        onClearSelection={selection.clear}
        onSaveToLibrary={() => { void currentLibraryHtml().then((html) => beginLibrarySave(html)) }}
        showBuild={controller.canBuild !== false}
      />
      {!exportOpen ? (
        <QaPhoneTitleActions
          ui={ui}
          canBuild={controller.canBuild !== false}
          activeInspectionId={workspace.activeInspectionId}
          allVisibleSelected={allVisibleSelected}
          onToggleSelectAll={onToggleSelectAll}
          inspectionCount={workspace.inspections.length}
        />
      ) : null}
      <QaPhoneSheetHost
        controller={controller}
        ui={ui}
        renameChecklistId={renameRoute.sheet}
        onRenameChecklistStarted={onRenameChecklistStarted}
        areaRows={areaRows}
        levelRows={levelRows}
        groups={groups}
        areaIds={areaIds}
        items={items}
        visibleIds={visibleIds}
        scope={scope === 'checks' ? 'checks' : scope}
        notes={notes}
        noteItem={noteItem}
        execution={execution}
        onRefresh={() => {
          workspace.invalidateExecution()
          queryClient.invalidateQueries({ queryKey: ['qa-inspections', props.jobId] })
          queryClient.invalidateQueries({ queryKey: ['job-quotes', props.jobId] })
          ui.closeSheet()
        }}
        onExport={openExport}
        onSaveToLibrary={() => { void currentLibraryHtml().then((html) => beginLibrarySave(html)) }}
      />
      {exportOpen && workspace.activeInspectionId ? (
        <QaItpExportView
          open
          onClose={() => setExportOpen(false)}
          api={api}
          jobId={props.jobId}
          orgId={props.orgId}
          inspectionId={workspace.activeInspectionId}
          executions={executions}
          jobNumber={props.jobId}
          request={itpRequest}
          defaultShowAll={!hasItpSelection}
          reportTitle={reportTitle}
          cover={cover}
          onSaveToLibrary={(html) => beginLibrarySave(html, itpRequest)}
        />
      ) : null}
      <QaItpLibraryNameDialog
        open={libraryOpen}
        defaultName={defaultItpLibraryName(executions.filter((area) => request.areaIds.includes(area.id)).map((area) => area.area_name))}
        busy={libraryBusy}
        error={libraryError}
        onCancel={() => { if (!libraryBusy) setLibraryOpen(false) }}
        onSave={(name) => {
          if (!libraryRequest || !workspace.activeInspectionId) return
          setLibraryBusy(true)
          void saveItpLibraryEntry(api, {
            inspectionId: workspace.activeInspectionId,
            name,
            html: libraryHtml,
            request: libraryRequest,
          }).then(() => {
            setLibraryOpen(false)
            void queryClient.invalidateQueries({ queryKey: ['itp-library', props.jobId] })
          }).catch((error: unknown) => {
            setLibraryError(error instanceof Error ? error.message : 'Could not save the ITP.')
          }).finally(() => setLibraryBusy(false))
        }}
      />
    </div>
    </QaDragRoot>
  )
}
